import { Checkbox } from '@/components/ui/checkbox'
import { FormField } from '@/components/ui/form-field'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  MONTH_FULL_NAMES,
  MONTH_YEAR_NONE_VALUE,
  PROFILE_YEAR_EXPIRY_FUTURE_SPAN,
  type MonthYearDraft,
  patchMonthYear,
  profileYearOptions,
  yearOptionsIncluding,
} from '@/lib/month-year'

function requiredLabel(label: string, required?: boolean): string {
  return required ? `${label} *` : label
}

export type MonthYearFieldProps = {
  idPrefix: string
  monthLabel: string
  yearLabel: string
  value?: MonthYearDraft
  onChange: (value: MonthYearDraft | undefined) => void
  required?: boolean
  disabled?: boolean
  /** Allow clearing a selected month or year (optional fields). */
  clearable?: boolean
  /** When true, year list includes future years (certificate expiry). */
  allowFutureYears?: boolean
}

export function MonthYearField({
  idPrefix,
  monthLabel,
  yearLabel,
  value,
  onChange,
  required = false,
  disabled = false,
  clearable = !required,
  allowFutureYears = false,
}: MonthYearFieldProps) {
  const monthId = `${idPrefix}-month`
  const yearId = `${idPrefix}-year`
  const monthValue = value?.month ? String(value.month) : undefined
  const yearValue = value?.year ? String(value.year) : undefined
  const years = yearOptionsIncluding(
    profileYearOptions(new Date(), {
      futureYears: allowFutureYears ? PROFILE_YEAR_EXPIRY_FUTURE_SPAN : 0,
    }),
    value?.year,
  )

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
      <FormField label={requiredLabel(monthLabel, required)} htmlFor={monthId}>
        <Select
          value={monthValue}
          onValueChange={(next) => {
            onChange(
              patchMonthYear(value, {
                month: next === MONTH_YEAR_NONE_VALUE ? null : Number(next),
              }),
            )
          }}
          disabled={disabled}
        >
          <SelectTrigger id={monthId} className="w-full" aria-required={required || undefined}>
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent>
            {clearable ? <SelectItem value={MONTH_YEAR_NONE_VALUE}>—</SelectItem> : null}
            {MONTH_FULL_NAMES.map((name, index) => (
              <SelectItem key={name} value={String(index + 1)}>
                {name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
      <FormField label={requiredLabel(yearLabel, required)} htmlFor={yearId}>
        <Select
          value={yearValue}
          onValueChange={(next) => {
            onChange(
              patchMonthYear(value, {
                year: next === MONTH_YEAR_NONE_VALUE ? null : Number(next),
              }),
            )
          }}
          disabled={disabled}
        >
          <SelectTrigger id={yearId} className="w-full" aria-required={required || undefined}>
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            {clearable ? <SelectItem value={MONTH_YEAR_NONE_VALUE}>—</SelectItem> : null}
            {years.map((year) => (
              <SelectItem key={year} value={String(year)}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
    </div>
  )
}

export type MonthYearRangeFieldsProps = {
  idPrefix: string
  ongoingLabel: string
  ongoingChecked: boolean
  onOngoingChange: (checked: boolean) => void
  start: MonthYearDraft | undefined
  end: MonthYearDraft | undefined
  onStartChange: (value: MonthYearDraft | undefined) => void
  onEndChange: (value: MonthYearDraft | undefined) => void
  startRequired?: boolean
  startMonthLabel?: string
  startYearLabel?: string
  endMonthLabel?: string
  endYearLabel?: string
  disabled?: boolean
  /** Future years on the end field only (certificate expiry). Start stays this year and earlier. */
  endAllowsFutureYears?: boolean
}

export function MonthYearRangeFields({
  idPrefix,
  ongoingLabel,
  ongoingChecked,
  onOngoingChange,
  start,
  end,
  onStartChange,
  onEndChange,
  startRequired = false,
  startMonthLabel = 'Start month',
  startYearLabel = 'Start year',
  endMonthLabel = 'End month',
  endYearLabel = 'End year',
  disabled = false,
  endAllowsFutureYears = false,
}: MonthYearRangeFieldsProps) {
  const ongoingId = `${idPrefix}-ongoing`

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Checkbox
          id={ongoingId}
          checked={ongoingChecked}
          disabled={disabled}
          onCheckedChange={(checked) => {
            const next = checked === true
            onOngoingChange(next)
            if (next) onEndChange(undefined)
          }}
        />
        <Label htmlFor={ongoingId} className="cursor-pointer font-normal">
          {ongoingLabel}
        </Label>
      </div>
      <MonthYearField
        idPrefix={`${idPrefix}-start`}
        monthLabel={startMonthLabel}
        yearLabel={startYearLabel}
        value={start}
        onChange={onStartChange}
        required={startRequired}
        disabled={disabled}
        clearable={!startRequired}
      />
      {ongoingChecked ? null : (
        <MonthYearField
          idPrefix={`${idPrefix}-end`}
          monthLabel={endMonthLabel}
          yearLabel={endYearLabel}
          value={end}
          onChange={onEndChange}
          required
          disabled={disabled}
          clearable={false}
          allowFutureYears={endAllowsFutureYears}
        />
      )}
    </div>
  )
}
